import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getAllUsers } from "../features/getAllUsersSlice";
import { editUser } from "../features/editUserSlice";
import { deleteUser } from "../features/deleteUserSlice";
import { getAllAWSAccounts } from "../features/getAllAWSAccounts";
import {
  Container,
  Card,
  Button,
  Table,
  Badge,
  Accordion,
  Form,
  Modal,
} from "react-bootstrap";
import Select from "react-select";
import makeAnimated from "react-select/animated";
import { FaEdit, FaTrashAlt, FaAws, FaPlus, FaSave, FaTimes } from "react-icons/fa";
// import { SiGooglecloud } from "react-icons/si";
import ToastNotification from "../reusable-components/ToastNotification";  // ✅ Import reusable toast

const animatedComponents = makeAnimated();

const buildPermsPayload = (cloudAccounts) => ({
  AWS: cloudAccounts.AWS.map((acc) => ({
    account_id: acc.account_id,
    app_tags: acc.app_tags,
  })),
  Azure: cloudAccounts.Azure.map((acc) => ({
    account_id: acc.account_id,
    app_tags: acc.app_tags,
  })),
  GCP: cloudAccounts.GCP.map((acc) => ({
    account_id: acc.account_id,
    app_tags: acc.app_tags,
  })),
});

const ManageUsers = () => {
  const dispatch = useDispatch();
  const { loading, users, error, errorMessage } = useSelector((state) => state.getAllUsers);
  const { accloading, accountsResponse, accerror, accerrorMessage } = useSelector((state) => state.getAllAWSAccounts);

  const [localUsers, setLocalUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [editRole, setEditRole] = useState("");
  const [editAccount, setEditAccount] = useState({ platform: "", index: null, account_id: "", tags: [] });
  const [accountError, setAccountError] = useState("");
  const [tagsError, setTagsError] = useState("");
  const [accountIdOptions, setAccountOptions] = useState([]);
  const [accountTags, setAccountTags] = useState([]);
  const [searchEmail, setSearchEmail] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const usersPerPage = 10;

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmMessage, setConfirmMessage] = useState("");
  const [onConfirmAction, setOnConfirmAction] = useState(() => {});

  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastVariant, setToastVariant] = useState("success");
  const [tagMenuOpen, setTagMenuOpen] = useState(false);

  useEffect(() => {
    dispatch(getAllUsers());
    dispatch(getAllAWSAccounts());
  }, [dispatch]);

  useEffect(() => {
    if (Array.isArray(users) && users.length) {
      const formatted = users.map((user) => ({
        username: user.email_id,
        role: user.role,
        cloud_accounts: {
          AWS: (user.perms?.AWS || []).map((acc) => ({
            account_id: acc.account_id,
            app_tags: acc.app_tags || [],
          })),
          Azure: (user.perms?.Azure || []).map((acc) => ({
            account_id: acc.account_id,
            app_tags: acc.app_tags || [],
          })),
          GCP: (user.perms?.GCP || []).map((acc) => ({
            account_id: acc.account_id,
            app_tags: acc.app_tags || [],
          })),
        },
      }));
      setLocalUsers(formatted);
    }
  }, [users]);

  useEffect(() => {
    if (accountsResponse && Array.isArray(accountsResponse.accounts)) {
      setAccountOptions(accountsResponse.accounts.map((acc) => ({
        value: acc.AccountId,
        label: `${acc.AccountName}-${acc.AccountId}`,
      })));
    } else if (Array.isArray(accountsResponse)) {
      setAccountOptions(accountsResponse.map((acc) => ({
        value: acc.AccountId,
        label: `${acc.AccountName}-${acc.AccountId}`,
      })));
    } else {
      setAccountOptions([]);
    }
  }, [accountsResponse]);

  useEffect(() => {
    if (editAccount.account_id !== "") {
      setAccountTags(
        accountsResponse.accounts
          .filter((account) => account.AccountId === editAccount.account_id)
          .map((account) => account.AccountTags)
          .flat()
          .map((tag) => ({ label: tag }))
      );
    }
  }, [editAccount, accountsResponse]);

 
  useEffect(() => {
    if (error) {
      setToastMessage(errorMessage || "Error loading users.");
      setToastVariant("danger");
      setShowToast(true);
    }
  }, [error, errorMessage]);

  useEffect(() => {
    if (accerror) {
      setToastMessage(accerrorMessage || "Error loading AWS accounts.");
      setToastVariant("danger");
      setShowToast(true);
    }
  }, [accerror, accerrorMessage]);

  const getFilteredAccountIdOptions = (platformKey, editIndex = null) => {
    const assigned = (selectedUser?.cloud_accounts?.[platformKey] || []).map((a) => a.account_id);
    if (editIndex !== null && selectedUser) {
      const current = selectedUser.cloud_accounts?.[platformKey]?.[editIndex]?.account_id;
      return accountIdOptions.filter((opt) => !assigned.includes(opt.value) || opt.value === current);
    }
    return accountIdOptions.filter((opt) => !assigned.includes(opt.value));
  };

  const confirmAction = (message, action) => {
    setConfirmMessage(message);
    setOnConfirmAction(() => action);
    setShowConfirmModal(true);
  };

  const handleEditUser = (user) => {
    const fullUser = localUsers.find((u) => u.username === user.username);
    if (!fullUser) return;
    setSelectedUser(fullUser);
    setEditRole(fullUser.role);
    setEditAccount({ platform: "", index: null, account_id: "", tags: [] });
    setAccountError("");
    setTagsError("");
  };

  const handleDeleteUser = (username) => {
    confirmAction(`Are you sure you want to delete user "${username}"?`, () => {
      setLocalUsers((prev) => prev.filter((user) => user.username !== username));
      dispatch(deleteUser({ email: username }));
      setToastMessage(`User "${username}" deleted successfully.`);
      setToastVariant("success");
      setShowToast(true);
    });
  };

  const handleCancelEdit = () => {
    setSelectedUser(null);
    setEditAccount({ platform: "", index: null, account_id: "", tags: [] });
    setAccountError("");
    setTagsError("");
  };

  const handleRoleSave = () => {
    const updatedUser = { ...selectedUser, role: editRole };
    setLocalUsers((prev) => prev.map((user) => user.username === updatedUser.username ? updatedUser : user));
    setSelectedUser(updatedUser);
    dispatch(editUser({
      email: updatedUser.username,
      role: editRole,
      perms: buildPermsPayload(updatedUser.cloud_accounts),
    }));
    setToastMessage(`User role updated to "${editRole}"`);
    setToastVariant("info");
    setShowToast(true);
  };

  const handleAccountEditClick = (platform, index) => {
    const acc = selectedUser.cloud_accounts[platform][index];
    setEditAccount({ platform, index, account_id: acc.account_id, tags: acc.app_tags });
    setAccountError("");
    setTagsError("");
  };

  const handleAddAccountClick = (platform) => {
    setEditAccount({ platform, index: null, account_id: "", tags: [] });
    setAccountError("");
    setTagsError("");
  };

  const handleDeleteAccount = (platform, index) => {
    confirmAction("Are you sure you want to delete this account?", () => {
      const updatedCloudAccounts = {
        ...selectedUser.cloud_accounts,
        [platform]: selectedUser.cloud_accounts[platform].filter((_, i) => i !== index),
      };
      const updatedUser = { ...selectedUser, cloud_accounts: updatedCloudAccounts };
      setLocalUsers((prev) => prev.map((user) => user.username === updatedUser.username ? updatedUser : user));
      setSelectedUser(updatedUser);
      dispatch(editUser({
        email: updatedUser.username,
        role: updatedUser.role,
        perms: buildPermsPayload(updatedCloudAccounts),
      }));
      setToastMessage(`Account removed successfully.`);
      setToastVariant("info");
      setShowToast(true);
    });
  };

  const cancelInlineEdit = () => {
    setEditAccount({ platform: "", index: null, account_id: "", tags: [] });
    setAccountError("");
    setTagsError("");
  };

  const handleTagKeyDown = (e) => {
    if (e.key === "Enter" && e.target.value.trim()) {
      e.preventDefault();
      addTag(e.target.value);
      e.target.value = "";
      setTagMenuOpen(false);
    }
  };

  const addTag = (tag) => {
    const t = (tag || "").toString().trim();
    if (!t) return;
    setEditAccount((prev) => {
      const exists = prev.tags.some((existing) => existing.toLowerCase() === t.toLowerCase());
      if (exists) return prev;
      return { ...prev, tags: [...prev.tags, t] };
    });
  };

  const removeTag = (tag) => {
    setEditAccount((prev) => ({ ...prev, tags: prev.tags.filter((t) => t !== tag) }));
  };

  const saveInlineAccountEdit = () => {
    let valid = true;
    if (!editAccount.account_id) {
      setAccountError("Account number is required");
      valid = false;
    } else {
      setAccountError("");
    }
    if (!editAccount.tags.length) {
      setTagsError("At least one tag is required");
      valid = false;
    } else {
      setTagsError("");
    }
    if (!valid) return;

    const updatedCloudAccounts = { ...selectedUser.cloud_accounts };
    if (editAccount.index !== null) {
      updatedCloudAccounts[editAccount.platform][editAccount.index] = {
        account_id: editAccount.account_id,
        app_tags: editAccount.tags,
      };
    } else {
      updatedCloudAccounts[editAccount.platform] = [
        ...updatedCloudAccounts[editAccount.platform],
        { account_id: editAccount.account_id, app_tags: editAccount.tags },
      ];
    }
    const updatedUser = { ...selectedUser, cloud_accounts: updatedCloudAccounts };
    setLocalUsers((prev) => prev.map((user) => user.username === updatedUser.username ? updatedUser : user));
    setSelectedUser(updatedUser);
    dispatch(editUser({
      email: updatedUser.username,
      role: updatedUser.role,
      perms: buildPermsPayload(updatedCloudAccounts),
    }));
    setToastMessage(`Account changes saved successfully.`);
    setToastVariant("success");
    setShowToast(true);
    cancelInlineEdit();
  };

  const platformConfig = [
    { key: "AWS", label: "AWS Accounts", icon: <FaAws color="#FF9900" size={22} />, bg: "#FFF4E5", headerBg: "#FFECD1" }
  ];

  const filteredUsers = localUsers.filter((u) => u.username.toLowerCase().includes(searchEmail.toLowerCase()));
  useEffect(() => {
    setCurrentPage(1); 
  }, [searchEmail]);
  const indexOfLastUser = currentPage * usersPerPage;
  const indexOfFirstUser = indexOfLastUser - usersPerPage;
  const currentUsers = filteredUsers.slice(indexOfFirstUser, indexOfLastUser);
  const totalPages = Math.ceil(filteredUsers.length / usersPerPage);

  if (loading || accloading) return <div className="text-center p-3">Loading...</div>;

  return (
    <Container fluid className="mt-4">
      
      <div >
        <ToastNotification show={showToast} onClose={() => setShowToast(false)} message={toastMessage} variant={toastVariant} />
      </div>

      <Modal show={showConfirmModal} onHide={() => setShowConfirmModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Action</Modal.Title>
        </Modal.Header>
        <Modal.Body>{confirmMessage}</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowConfirmModal(false)}>Cancel</Button>
          <Button variant="danger" onClick={() => { onConfirmAction(); setShowConfirmModal(false); }}>Yes, Proceed</Button>
        </Modal.Footer>
      </Modal>


      <h4>Manage User</h4>
      <hr />

      {!selectedUser && (
        <Form.Control
          type="text"
          placeholder="Search by email..."
          value={searchEmail}
          onChange={(e) => setSearchEmail(e.target.value)}
          className="mb-3"
        />
      )}

      {!selectedUser ? (
        <Card className="card border-dark mb-3">
          <Container className="card-header">User List</Container>
          <Card.Body>
            <Table hover responsive>
              <thead style={{ backgroundColor: "#e9ecef" }}>
                <tr>
                  <th>Email</th>
                  <th>Role</th>
                  <th className="text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {currentUsers.map((user, idx) => (
                  <tr key={idx}>
                    <td>{user.username}</td>
                    <td>
                      {user.role?.toLowerCase() === "user" && (
                        <Badge pill bg="danger">
                          {user.role}
                        </Badge>
                      )}
                      {user.role?.toLowerCase() === "admin" && (
                        <Badge pill bg="info">
                          {user.role}
                        </Badge>
                      )}
                    </td>
                    <td className="text-center">
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => handleEditUser(user)}
                        className="me-2"
                      >
                        <FaEdit />
                      </Button>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => handleDeleteUser(user.username)}
                      >
                        <FaTrashAlt />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
            {filteredUsers.length > 0 ? (
                <div className="d-flex justify-content-between">
                  <span>
                    Page {currentPage} of {totalPages}
                  </span>
                  <div>
                    <Button
                      variant="secondary"
                      size="sm"
                      disabled={currentPage === 1}
                      onClick={() => setCurrentPage((prev) => prev - 1)}
                      className="me-2"
                    >
                      Prev
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      disabled={currentPage === totalPages}
                      onClick={() => setCurrentPage((prev) => prev + 1)}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="text-center text-muted">No users found.</div>
              )}
          </Card.Body>
        </Card>
      ) : (
        <Card className="card border-dark mb-3">
          <Container className="d-flex justify-content-between align-items-center fw-bold card-header">
            <div>
              Editing: <strong>{selectedUser.username}</strong>
            </div>
            <Button variant="light" size="sm" onClick={handleCancelEdit}>
              Cancel
            </Button>
          </Container>
          <Card.Body style={{ backgroundColor: "#f9f9f9" }}>
            <div className="mb-3 d-flex align-items-center">
              <Form.Label className="me-2 fw-bold mb-0">Role:</Form.Label>
              <Form.Select
                value={editRole}
                onChange={(e) => setEditRole(e.target.value)}
                size="sm"
                style={{ maxWidth: "200px" }}
              >
                <option value="user">User</option>
                <option value="admin">Admin</option>
              </Form.Select>
              <Button
                variant="primary"
                size="sm"
                className="ms-2"
                onClick={handleRoleSave}
              >
                Save Role
              </Button>
            </div>

            <Accordion alwaysOpen>
              {platformConfig.map((platform) => (
                <Accordion.Item eventKey={platform.key} key={platform.key}>
                  <Accordion.Header>
                    {platform.icon}
                    <span className="ms-2">{platform.label}</span>
                  </Accordion.Header>
                  <Accordion.Body style={{ backgroundColor: platform.bg }}>
                    <Table bordered hover>
                      <thead style={{ backgroundColor: platform.headerBg }}>
                        <tr>
                          <th style={{ width: "35%" }}>Account</th>
                          <th style={{ width: "45%", maxWidth: "300px" }}>
                            Tags
                          </th>
                          <th style={{ width: "20%" }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedUser.cloud_accounts[platform.key].map(
                          (acc, idx) => (
                            <tr key={idx}>
                              <td>
                                {editAccount.platform === platform.key &&
                                editAccount.index === idx ? (
                                  <Select
                                    options={getFilteredAccountIdOptions(
                                      platform.key,
                                      idx
                                    )}
                                    placeholder="Select account..."
                                    value={
                                      accountIdOptions.find(
                                        (opt) =>
                                          opt.value === editAccount.account_id
                                      ) || null
                                    }
                                    onChange={(selected) =>
                                      setEditAccount((prev) => ({
                                        ...prev,
                                        account_id: selected.value,
                                        tags: [], // Reset tags when account changes
                                      }))
                                    }
                                    components={animatedComponents}
                                  />
                                ) : (
                                  accountIdOptions.find(
                                    (opt) => opt.value === acc.account_id
                                  )?.label || acc.account_id
                                )}
                                {editAccount.platform === platform.key &&
                                  editAccount.index === idx &&
                                  accountError && (
                                    <div className="text-danger small">
                                      {accountError}
                                    </div>
                                  )}
                              </td>
                              {/* <td>
                                {editAccount.platform === platform.key &&
                                editAccount.index === idx ? (
                                  <>
                                    <Form.Control
                                      type="text"
                                      placeholder="Type tag and press Enter"
                                      onKeyDown={handleTagKeyDown}
                                    />
                                    <div className="mt-1">
                                      {editAccount.tags.map((tag, tIdx) => (
                                        <Badge
                                          key={tIdx}
                                          bg="secondary"
                                          className="me-1"
                                        >
                                          {tag}{" "}
                                          <span
                                            style={{ cursor: "pointer" }}
                                            onClick={() => removeTag(tag)}
                                          >
                                            ×
                                          </span>
                                        </Badge>
                                      ))}
                                    </div>
                                    {tagsError && (
                                      <div className="text-danger small">
                                        {tagsError}
                                      </div>
                                    )}
                                  </>
                                ) : (
                                  acc.app_tags.join(", ")
                                )}
                              </td> */}
                              <td
                                style={{
                                  maxWidth: "300px",
                                  wordWrap: "break-word",
                                  whiteSpace: "normal",
                                }}
                              >
                                {editAccount.platform === platform.key &&
                                editAccount.index === idx ? (
                                  <>
                                    <Select
                                      options={accountTags.filter(
                                        (opt) =>
                                          !editAccount.tags.some(
                                            (t) =>
                                              t.toLowerCase() ===
                                              (opt.label || "").toLowerCase()
                                          )
                                      )}
                                      placeholder="Type tags..."
                                      value={null}
                                      onChange={(selected) => {
                                        if (selected) addTag(selected.label);
                                        setTagMenuOpen(false);
                                      }}
                                      onKeyDown={handleTagKeyDown}
                                      components={animatedComponents}
                                      menuIsOpen={tagMenuOpen}           
                                      onMenuOpen={() => setTagMenuOpen(true)}
                                      onMenuClose={() => setTagMenuOpen(false)}
                                    />
                                    <div className="mt-1">
                                      {editAccount.tags.map((tag, tIdx) => (
                                        <Badge
                                          key={tIdx}
                                          bg="secondary"
                                          className="me-1"
                                        >
                                          {tag}{" "}
                                          <span
                                            style={{ cursor: "pointer" }}
                                            onClick={() => removeTag(tag)}
                                          >
                                            ×
                                          </span>
                                        </Badge>
                                      ))}
                                    </div>
                                    {tagsError && (
                                      <div className="text-danger small">
                                        {tagsError}
                                      </div>
                                    )}
                                    {accountError && (
                                      <div className="text-danger small">
                                        {accountError}
                                      </div>
                                    )}
                                  </>
                                ) : (
                                  acc.app_tags.join(", ")
                                )}
                              </td>
                              <td>
                                {editAccount.platform === platform.key &&
                                editAccount.index === idx ? (
                                  <>
                                    <Button
                                      variant="success"
                                      size="sm"
                                      className="me-1"
                                      onClick={saveInlineAccountEdit}
                                    >
                                      <FaSave /> Save
                                    </Button>
                                    <Button
                                      variant="secondary"
                                      size="sm"
                                      onClick={cancelInlineEdit}
                                    >
                                      <FaTimes /> Cancel
                                    </Button>
                                  </>
                                ) : (
                                  <>
                                    <Button
                                      variant="outline-primary"
                                      size="sm"
                                      className="me-1"
                                      onClick={() =>
                                        handleAccountEditClick(
                                          platform.key,
                                          idx
                                        )
                                      }
                                    >
                                      <FaEdit />
                                    </Button>
                                    <Button
                                      variant="outline-danger"
                                      size="sm"
                                      onClick={() =>
                                        handleDeleteAccount(platform.key, idx)
                                      }
                                    >
                                      <FaTrashAlt />
                                    </Button>
                                  </>
                                )}
                              </td>
                            </tr>
                          )
                        )}
                        {editAccount.platform === platform.key &&
                        editAccount.index === null ? (
                          <tr>
                            <td>
                              <Select
                                options={getFilteredAccountIdOptions(
                                  platform.key
                                )}
                                placeholder="Select account..."
                                value={
                                  accountIdOptions.find(
                                    (opt) =>
                                      opt.value === editAccount.account_id
                                  ) || null
                                }
                                onChange={(selected) => {
                                  setEditAccount((prev) => ({
                                    ...prev,
                                    account_id: selected.value,
                                    tags: [], // Reset tags when account changes
                                  }));
                                }}
                                components={animatedComponents}
                              />
                              {accountError && (
                                <div className="text-danger small">
                                  {accountError}
                                </div>
                              )}
                            </td>
                            {/* <td>
                              <Form.Control
                                type="text"
                                placeholder="Type tag and press Enter"
                                onKeyDown={handleTagKeyDown}
                              />
                              <div className="mt-1">
                                {editAccount.tags.map((tag, tIdx) => (
                                  <Badge
                                    key={tIdx}
                                    bg="secondary"
                                    className="me-1"
                                  >
                                    {tag}{" "}
                                    <span
                                      style={{ cursor: "pointer" }}
                                      onClick={() => removeTag(tag)}
                                    >
                                      ×
                                    </span>
                                  </Badge>
                                ))}
                              </div>
                              {tagsError && (
                                <div className="text-danger small">
                                  {tagsError}
                                </div>
                              )}
                            </td> */}
                            <td
                              style={{
                                maxWidth: "300px",
                                wordWrap: "break-word",
                                whiteSpace: "normal",
                              }}
                            >
                              <Select
                                options={accountTags.filter(
                                  (opt) =>
                                    !editAccount.tags.some(
                                      (t) =>
                                        t.toLowerCase() ===
                                        (opt.label || "").toLowerCase()
                                    )
                                )}
                                placeholder="Type tags..."
                                value={null}
                                onChange={(selected) => {
                                  if (selected) addTag(selected.label);
                                }}
                                onKeyDown={handleTagKeyDown}
                                components={animatedComponents}
                              />
                              <div className="mt-1">
                                {editAccount.tags.map((tag, tIdx) => (
                                  <Badge
                                    key={tIdx}
                                    bg="secondary"
                                    className="me-1"
                                  >
                                    {tag}{" "}
                                    <span
                                      style={{ cursor: "pointer" }}
                                      onClick={() => removeTag(tag)}
                                    >
                                      ×
                                    </span>
                                  </Badge>
                                ))}
                              </div>
                              {tagsError && (
                                <div className="text-danger small">
                                  {tagsError}
                                </div>
                              )}
                              {accountError && (
                                <div className="text-danger small">
                                  {accountError}
                                </div>
                              )}
                            </td>
                            <td>
                              <Button
                                variant="success"
                                size="sm"
                                className="me-1"
                                onClick={saveInlineAccountEdit}
                              >
                                <FaSave /> Save
                              </Button>
                              <Button
                                variant="secondary"
                                size="sm"
                                onClick={cancelInlineEdit}
                              >
                                <FaTimes /> Cancel
                              </Button>
                            </td>
                          </tr>
                        ) : (
                          <tr>
                            <td colSpan="3" className="text-end">
                              <Button
                                size="sm"
                                variant="success"
                                onClick={() =>
                                  handleAddAccountClick(platform.key)
                                }
                              >
                                <FaPlus /> Add Account
                              </Button>
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </Table>
                  </Accordion.Body>
                </Accordion.Item>
              ))}
            </Accordion>
          </Card.Body>
        </Card>
      )}
    </Container>
  );
};

export default ManageUsers;